import {
  FITNESS_WORKOUT_FINISH_RPC,
  FitnessWorkoutFinishError,
  canonicalFitnessWorkoutFinishRequest,
  fitnessWorkoutFinishId,
  hasVerifiedFitnessWorkoutFinishFailedReceipt,
  hasVerifiedFitnessWorkoutFinishMissingReceipt,
  hasVerifiedFitnessWorkoutFinishRead,
  hasVerifiedFitnessWorkoutFinishSuccess,
  type FitnessWorkoutFinishCommitRequest,
  type FitnessWorkoutFinishFailedReceipt,
  type FitnessWorkoutFinishReply,
  type FitnessWorkoutFinishRequest,
  type FitnessWorkoutFinishSuccess,
} from '../../supabase/functions/_shared/fitnessWorkoutFinishDomain';

export interface FitnessWorkoutFinishAuth {
  getUser(jwt?: string): PromiseLike<{ data: { user: { id: string; is_anonymous?: boolean } | null }; error: unknown }>;
  getSession(): PromiseLike<{ data: { session: { user: { id: string; is_anonymous?: boolean }; access_token: string } | null }; error: unknown }>;
}
export interface FitnessWorkoutFinishGatewayOptions {
  ownerId: string;
  auth: FitnessWorkoutFinishAuth;
  isCurrent: () => boolean | void;
  /** Receipt recovery must retain the original reviewed commit, never reconstruct it. */
  expectedRequest?: FitnessWorkoutFinishCommitRequest;
}

type RpcResult = { data: unknown; error: unknown };
type RpcBuilder = PromiseLike<RpcResult> & { setHeader(name: string, value: string): RpcBuilder };
const successes = new WeakSet<object>();
const failures = new WeakSet<object>();
const notDispatched = new WeakMap<object, { owner: string; request: FitnessWorkoutFinishCommitRequest }>();
// A second same-operation invocation permanently prevents release authority, including old errors.
// Only identity keys and a repeat marker are retained; no eviction can revive an old first attempt.
const commitAttempts = new Map<string, boolean>();
let clientPromise: Promise<typeof import('@/integrations/supabase/client')> | undefined;
const loadClient = () => clientPromise ??= import('@/integrations/supabase/client');

export function isGenuineFitnessWorkoutFinishProof(value: unknown): value is FitnessWorkoutFinishSuccess {
  return !!value && typeof value === 'object' && successes.has(value);
}
export function isGenuineFitnessWorkoutFinishFailedReceipt(value: unknown): value is FitnessWorkoutFinishFailedReceipt {
  return !!value && typeof value === 'object' && failures.has(value);
}
function stable(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stable);
  if (value && typeof value === 'object') return Object.fromEntries(Object.keys(value).sort()
    .map(key => [key, stable((value as Record<string, unknown>)[key])]));
  return value;
}
/** Only an actual failed gateway invocation before construction of its RPC request may release its own claim. */
export function isGenuineFitnessWorkoutFinishNotDispatched(
  error: unknown, input: FitnessWorkoutFinishCommitRequest, ownerId: string,
): boolean {
  if (!error || typeof error !== 'object') return false;
  const known = notDispatched.get(error);
  if (!known) return false;
  try {
    const owner = fitnessWorkoutFinishId(ownerId), request = canonicalFitnessWorkoutFinishRequest(input, owner);
    return request.action === 'commit' && owner === known.owner && commitAttempts.get(owner + ':' + request.operation_id) === false &&
      JSON.stringify(stable(request)) === JSON.stringify(stable(known.request));
  } catch { return false; }
}

function freeze<T>(value: T): T {
  if (value && typeof value === 'object') {
    for (const child of Object.values(value)) freeze(child);
    Object.freeze(value);
  }
  return value;
}
const sameContext = (a: FitnessWorkoutFinishRequest, b: FitnessWorkoutFinishCommitRequest) =>
  a.action !== 'read' && a.operation_id === b.operation_id &&
  (a.mirror_context === null ? b.mirror_context === null : b.mirror_context !== null &&
    a.mirror_context.session_id === b.mirror_context.session_id && a.mirror_context.action_id === b.mirror_context.action_id);
const fail = (code: string): never => { throw new FitnessWorkoutFinishError(code); };

/** Authenticated read/commit/lookup through the real SDK, with a per-request pinned JWT. */
export async function invokeFitnessWorkoutFinish(
  input: FitnessWorkoutFinishRequest,
  options: FitnessWorkoutFinishGatewayOptions,
): Promise<FitnessWorkoutFinishReply> {
  // All caller-controlled references and identity primitives are captured before the first await.
  const owner = fitnessWorkoutFinishId(options.ownerId);
  const request = freeze(canonicalFitnessWorkoutFinishRequest(input, owner));
  const attemptKey = request.action === 'commit' ? owner + ':' + request.operation_id : null;
  if (attemptKey !== null) commitAttempts.set(attemptKey, commitAttempts.has(attemptKey));
  let rpcCalled = false;
  try {
    const auth = options.auth;
    const getUser: FitnessWorkoutFinishAuth['getUser'] = auth.getUser.bind(auth);
    const getSession: FitnessWorkoutFinishAuth['getSession'] = auth.getSession.bind(auth);
    const isCurrent = options.isCurrent;
    if (typeof isCurrent !== 'function') return fail('invalid_session');
    const suppliedExpected = options.expectedRequest;
    const expected = request.action === 'commit' ? request : suppliedExpected === undefined ? undefined :
      freeze(canonicalFitnessWorkoutFinishRequest(suppliedExpected, owner));
    if (request.action === 'receipt' && (!expected || expected.action !== 'commit' || !sameContext(request, expected))) {
      return fail('original_request_required');
    }
    const assert = () => { if (isCurrent() === false) fail('invalid_session'); };
    const checked = async <T>(call: () => PromiseLike<T>): Promise<T> => {
      assert();
      const result = await call();
      assert();
      return result;
    };
    const verifyUser = async (token?: string) => {
      const result = await checked(() => getUser(token));
      if (result.error || result.data.user?.id !== owner || result.data.user.is_anonymous === true) fail('invalid_session');
    };
    const verifySession = async () => {
      const result = await checked(getSession);
      const session = result.data.session;
      if (result.error || session?.user.id !== owner || session.user.is_anonymous === true ||
        typeof session.access_token !== 'string' || !/^[!-~]{1,16000}$/.test(session.access_token)) return fail('invalid_session');
      return session.access_token;
    };
    // A fixed import keeps unrelated runtime imports independent of browser localStorage.
    // Bind the actual SDK method after the guarded import, before any authentication await.
    const { supabase } = await checked(loadClient);
    assert();
    const rpc = supabase.rpc.bind(supabase) as unknown as (name: string, args: { p_request: FitnessWorkoutFinishRequest }) => RpcBuilder;
    await verifyUser();
    const token = await verifySession();
    // Renew server authentication and local owner immediately before dispatch, retaining the original JWT.
    await verifyUser(token);
    await verifySession();
    assert();
    const result = await checked(() => {
      // Builder construction is the conservative boundary, including setHeader/SDK/fetch errors.
      rpcCalled = true;
      return rpc(FITNESS_WORKOUT_FINISH_RPC, { p_request: request }).setHeader('Authorization', 'Bearer ' + token);
    });
    await verifyUser(token);
    await verifySession();
    assert();
    if (result.error) return fail('outcome_unknown');
    const reply = result.data;
    if (request.action === 'read' && hasVerifiedFitnessWorkoutFinishRead(request, reply, owner)) {
      return freeze(JSON.parse(JSON.stringify(reply)) as FitnessWorkoutFinishReply);
    }
    if (expected?.action === 'commit' && hasVerifiedFitnessWorkoutFinishSuccess(expected, reply, owner) &&
      reply.action === request.action) {
      const proof = freeze(JSON.parse(JSON.stringify(reply)) as FitnessWorkoutFinishSuccess);
      successes.add(proof);
      return proof;
    }
    if (expected?.action === 'commit' && hasVerifiedFitnessWorkoutFinishFailedReceipt(expected, reply, owner) &&
      reply.action === request.action) {
      const proof = freeze(JSON.parse(JSON.stringify(reply)) as FitnessWorkoutFinishFailedReceipt);
      failures.add(proof);
      return proof;
    }
    if (request.action === 'receipt' && hasVerifiedFitnessWorkoutFinishMissingReceipt(request, reply, owner)) {
      return freeze(JSON.parse(JSON.stringify(reply)) as FitnessWorkoutFinishReply);
    }
    // An unreceipted error is not authority to remove a journal entry or retry a write.
    if (reply && typeof reply === 'object' && Object.keys(reply).length === 4 &&
      Object.keys(reply).every(key => ['ok', 'version', 'error', 'safe_retry'].includes(key))) {
      const error = reply as { ok?: unknown; version?: unknown; error?: unknown; safe_retry?: unknown };
      if (error.ok === false && error.version === 1 && typeof error.error === 'string' &&
        /^[a-z][a-z0-9_]{0,100}$/.test(error.error) && typeof error.safe_retry === 'boolean') {
        return freeze(JSON.parse(JSON.stringify(error)) as FitnessWorkoutFinishReply);
      }
    }
    return fail('outcome_unknown');
  } catch (error) {
    if (!rpcCalled && request.action === 'commit' && commitAttempts.get(attemptKey!) === false) {
      const message = error && typeof error === 'object' ? Object.getOwnPropertyDescriptor(error, 'message')?.value : undefined;
      // A unique gateway-thrown error prevents re-used caller Error objects from inheriting authority.
      const known = new FitnessWorkoutFinishError(typeof message === 'string' && /^[a-z][a-z0-9_]{0,100}$/.test(message)
        ? message : 'outcome_not_dispatched');
      notDispatched.set(known, { owner, request });
      throw known;
    }
    throw error;
  }
}
